import { PartialMessage } from '@bufbuild/protobuf';
import {
  deserify,
  getFirebaseApp,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import {
  ContentAttempt,
  LiveQuizSession,
  SessionStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import { HOME } from '../../routeHandling/RoutesNomenclature';
import { setToastInfo } from '../../pages/Home/reducer/homeDashboard.slice';
import {
  resetLiveQuiz as resetLiveQuizAction,
  setCurrentQuestion,
  setFinalMetrics,
  setFirestoreCustomToken,
  setHasAnsweredCurrent,
  setLastQuestionStats,
  setLiveQuizJoinedCount,
  setLiveQuizQuestions,
  setLiveQuizSession,
  setLiveQuizSessions,
  setRevealedResult,
  setSessionStatus,
} from '../../pages/LiveQuiz/reducer/liveQuiz.slice';
import { LiveQuizEndedDialog } from '../../pages/LiveQuiz/components/LiveQuizEndedDialog';

const db = getFirestore(getFirebaseApp());

interface IContextData {
  // Returns every session currently live across the student's sections —
  // usually zero or one, occasionally more (a student in more than one
  // section, each with its own teacher independently starting a quiz).
  checkForActiveLiveQuiz: (studentId: bigint) => Promise<LiveQuizSession[]>;
  // Takes the full session (not just its id) — on success this is the point
  // that commits it as THE joined session (live_quiz_session, singular) the
  // rest of the flow (Waiting Room/Play) reads from, distinct from the
  // discovery list a student may see more than one entry in.
  joinLiveQuizSession: (
    studentId: bigint,
    session: LiveQuizSession
  ) => Promise<boolean>;
  submitLiveQuizResponse: (params: {
    studentId: bigint;
    liveQuizSessionId: bigint;
    liveQuizQuestionSessionId: bigint;
    questionId: string;
    response: PartialMessage<ContentAttempt>;
  }) => Promise<boolean>;
  refreshStudentState: (
    studentId: bigint,
    liveQuizSessionId: bigint
  ) => Promise<void>;
  resetLiveQuizData: () => void;
}

const liveQuizContext = createContext<IContextData | undefined>(undefined);
const { Provider } = liveQuizContext;

interface IProviderProps {
  children: ReactNode;
}

// Student-side real-time layer for LiveQuiz — this is genuinely new territory
// for the student app (no Firestore usage exists here today). The student
// client never writes Firestore, and never decides for itself what question
// it's allowed to answer; it only ever renders whatever this listener says
// the session's current question is, with submitLiveQuizResponse as the
// actual (server-side) lockstep/reveal enforcement.
export const LiveQuizContextProvider: React.FC<IProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { live_quiz_session, firestore_custom_token, session_status } =
    deserify(useAppSelector((state) => state.liveQuiz));
  const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const {
    LmsLiveQuizStudentAPIServiceV1ClientWithStatusCodeHandler:
      liveQuizStudentClient,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler:
      assessmentCommonClient,
  } = useCommonServiceClientContext();
  const [authReady, setAuthReady] = useState(false);
  const firestoreDocId = live_quiz_session?.firestoreDocId;
  // Live Quiz receives stripped question content only. Retain the section
  // mapping fetched from the assessment separately so response submissions
  // can be attributed to the right subject in multi-subject assessments.
  const questionSectionNosRef = React.useRef<Record<string, number>>({});
  const questionSectionNosTaskIdRef = React.useRef<string | undefined>(
    undefined
  );

  const fetchQuestionSectionNos = React.useCallback(
    async (studentId: bigint, taskId: string) => {
      try {
        const res = /^\d+$/.test(taskId)
          ? await assessmentCommonClient.fetchAssessmentContent({
              personId: studentId,
              personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
              assessmentId: BigInt(taskId),
            })
          : await assessmentCommonClient.fetchAssessmentContentByStringId({
              personId: studentId,
              personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
              assessmentStringId: taskId,
            });
        const taskQuestionInfo =
          res.data?.questionsSequenceInfo?.taskQuestionInfo ?? [];

        questionSectionNosRef.current = taskQuestionInfo.reduce<
          Record<string, number>
        >((sectionNos, questionInfo) => {
          if (questionInfo.questionId && questionInfo.sectionNo !== undefined) {
            sectionNos[questionInfo.questionId] = questionInfo.sectionNo;
          }
          return sectionNos;
        }, {});
        questionSectionNosTaskIdRef.current = taskId;
      } catch (err) {
        console.log('fetchAssessmentContent for Live Quiz error', err);
        questionSectionNosRef.current = {};
        questionSectionNosTaskIdRef.current = undefined;
      }
    },
    [assessmentCommonClient]
  );

  // liveQuiz is persisted in Redux but the lookup above deliberately stays
  // in memory. Rebuild it after a reload before the student submits a
  // response; otherwise sectionNo would be omitted for a multi-subject quiz.
  useEffect(() => {
    const studentId = userInfo?.studentProfileId;
    const taskId = live_quiz_session?.taskId;
    if (
      !studentId ||
      !taskId ||
      questionSectionNosTaskIdRef.current === taskId
    ) {
      return;
    }
    void fetchQuestionSectionNos(studentId, taskId);
  }, [
    fetchQuestionSectionNos,
    live_quiz_session?.taskId,
    userInfo?.studentProfileId,
  ]);

  useEffect(() => {
    if (!firestore_custom_token) return;
    signInWithCustomToken(getAuth(getFirebaseApp()), firestore_custom_token)
      .then(() => setAuthReady(true))
      .catch((err) => console.log('LiveQuiz signInWithCustomToken error', err));
  }, [firestore_custom_token]);

  // Resync from Postgres (via getLiveQuizStudentState) rather than trusting
  // any locally-held "next question" state — the Firestore doc only tells us
  // *when* to refetch, never what to render directly.
  const refreshStudentState = async (
    studentId: bigint,
    liveQuizSessionId: bigint
  ) => {
    try {
      const res = await liveQuizStudentClient.getLiveQuizStudentState({
        studentId,
        liveQuizSessionId,
      });
      if (res.status === 200) {
        dispatch(setSessionStatus(res.sessionStatus));
        dispatch(setCurrentQuestion(res.currentQuestion));
        dispatch(setLiveQuizQuestions(res.questions));
        dispatch(setHasAnsweredCurrent(res.hasAnsweredCurrent));
        dispatch(setRevealedResult(res.revealedResult));
        dispatch(setFinalMetrics(res.finalMetrics));
      }
    } catch (err) {
      console.log('getLiveQuizStudentState error', err);
    }
  };

  useEffect(() => {
    if (!firestoreDocId || !authReady) return;
    const unsub = onSnapshot(
      doc(db, 'liveQuizSessions', firestoreDocId),
      (snap) => {
        // The session doc doesn't carry full question content (text/options)
        // — only ids/sequence/status. It's purely the trigger for *when* to
        // re-fetch full state; the fetch itself is the source of truth.
        if (live_quiz_session && userInfo?.studentProfileId) {
          refreshStudentState(
            userInfo.studentProfileId,
            live_quiz_session.liveQuizSessionId
          );
        }
        // joinedCount/lastQuestionStats are cheap to read straight off this
        // same doc — a class-wide "how did we do" the student can see
        // without needing per-student response docs (which would leak who
        // else answered what).
        const data = snap.data();
        if (data) {
          dispatch(setLiveQuizJoinedCount(data.joinedCount ?? undefined));
          dispatch(setLastQuestionStats(data.lastQuestionStats ?? undefined));
        }
      },
      (err) => console.log('LiveQuiz session doc listener error', err)
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firestoreDocId, authReady, dispatch]);

  // The teacher navigating away from the live quiz screens pauses the
  // session server-side (see the teacher's pause-on-unmount hook) — that
  // change reaches here via the same session-doc listener above (it
  // triggers refreshStudentState, which is what actually sets
  // session_status from Postgres). Reset local state and send the student
  // back to Home rather than leaving them stuck on a frozen question/
  // waiting screen — Home's own live-session discovery re-finds it and
  // shows "Join Now" again the moment the teacher resumes.
  //
  // Deliberately NOT reacting to SESSION_STATUS_COMPLETED here too: a normal
  // end-of-quiz is exactly when a student is most likely mid-reveal on the
  // last question (see displayedQuestion/revealed_result handling in
  // LiveQuizAnswer.tsx, added after a prior bug where a premature redirect
  // cut that screen off) — force-navigating on every COMPLETED would
  // reintroduce that. The same-teacher "stuck session" cleanup this pairs
  // with only ever targets sessions nobody is actively connected to, so
  // there's no real student-facing gap being left open by skipping it here.
  useEffect(() => {
    if (session_status === SessionStatusEnum.SESSION_STATUS_PAUSED) {
      dispatch(
        setToastInfo({
          label:
            'Your teacher has paused the live quiz. The session will appear on your home screen when it resumes.',
          variant: 'info',
          open: true,
        })
      );
      resetLiveQuizData();
      navigate(HOME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session_status]);

  const checkForActiveLiveQuiz = async (studentId: bigint) => {
    try {
      const res = await liveQuizStudentClient.getActiveLiveQuizForStudent({
        studentId,
      });
      if (res.status === 200) {
        // Defensive fallback if this ever hits a not-yet-restarted backend
        // that predates the repeated field (proto3 default is already [],
        // but belt-and-suspenders costs nothing here).
        const sessions = res.liveQuizSessions ?? [];
        dispatch(setLiveQuizSessions(sessions));
        return sessions;
      }
    } catch (err) {
      console.log('getActiveLiveQuizForStudent error', err);
    }
    return [];
  };

  const joinLiveQuizSession = async (
    studentId: bigint,
    session: LiveQuizSession
  ) => {
    try {
      const res = await liveQuizStudentClient.joinLiveQuizSession({
        studentId,
        liveQuizSessionId: session.liveQuizSessionId,
      });
      if (res.status === 200) {
        await fetchQuestionSectionNos(studentId, session.taskId);
        dispatch(setLiveQuizSession(session));
        dispatch(setFirestoreCustomToken(res.firestoreCustomToken));
        dispatch(setCurrentQuestion(res.currentQuestion));
        dispatch(setLiveQuizQuestions(res.questions));
        return true;
      }
      dispatch(
        setToastInfo({
          label: 'Could not join the live quiz. Please try again.',
          variant: 'error',
          open: true,
        })
      );
      return false;
    } catch (err) {
      console.log('joinLiveQuizSession error', err);
      dispatch(
        setToastInfo({
          label: 'Could not join the live quiz. Please try again.',
          variant: 'error',
          open: true,
        })
      );
      return false;
    }
  };

  const submitLiveQuizResponse: IContextData['submitLiveQuizResponse'] = async (
    params
  ) => {
    try {
      // The reload effect above may still be in flight when a student answers.
      // Resolve the mapping here as well, so a response never races it and
      // loses sectionNo for a multi-subject assessment.
      const taskId = live_quiz_session?.taskId;

      if (
        taskId &&
        (questionSectionNosTaskIdRef.current !== taskId ||
          questionSectionNosRef.current[params.questionId] === undefined)
      ) {
        await fetchQuestionSectionNos(
          params.studentId,
          live_quiz_session.taskId
        );
      }
      const res = await liveQuizStudentClient.submitLiveQuizResponse({
        ...params,
        sectionNo: questionSectionNosRef.current[params.questionId],
      });
      if (res.status !== 200) {
        dispatch(
          setToastInfo({
            label: 'Could not submit your answer. Please try again.',
            variant: 'error',
            open: true,
          })
        );
      }
      return res.status === 200;
    } catch (err) {
      console.log('submitLiveQuizResponse error', err);
      dispatch(
        setToastInfo({
          label: 'Could not submit your answer. Please try again.',
          variant: 'error',
          open: true,
        })
      );
      return false;
    }
  };

  const resetLiveQuizData = () => {
    setAuthReady(false);
    questionSectionNosRef.current = {};
    questionSectionNosTaskIdRef.current = undefined;
    // Reset Redux state
    dispatch(resetLiveQuizAction());
  };

  const contextValue: IContextData = {
    checkForActiveLiveQuiz,
    joinLiveQuizSession,
    submitLiveQuizResponse,
    refreshStudentState,
    resetLiveQuizData,
  };

  return (
    <Provider value={contextValue}>
      {children}
      <LiveQuizEndedDialog />
    </Provider>
  );
};

export const useLiveQuizContext = () => {
  const context = useContext(liveQuizContext);
  if (!context) {
    throw new Error(
      'useLiveQuizContext must be used within a LiveQuizContextProvider'
    );
  }
  return context;
};
